import { normalizeMessageBodyForRender } from "./messageBodyHtml";

export type PrintMessageDocumentArgs = {
  title: string;
  bodyHtml: string;
};

export type BrowserDownloadItem = {
  downloadUrl: string;
  fileName?: string | null;
};

const PRINT_STYLES = `
  body {
    margin: 0;
    padding: 40px;
    color: #111;
    font-family: Poppins, Arial, sans-serif;
    line-height: 1.6;
  }

  .print-message__title {
    margin: 0 0 24px;
    font-size: 28px;
    line-height: 1.2;
    font-weight: 600;
  }

  .print-message__body {
    font-size: 16px;
    line-height: 1.7;
    word-break: break-word;
  }

  .print-message__body > *:first-child {
    margin-top: 0;
  }

  .print-message__body > *:last-child {
    margin-bottom: 0;
  }

  .print-message__body p,
  .print-message__body ul,
  .print-message__body ol,
  .print-message__body blockquote,
  .print-message__body h1,
  .print-message__body h2,
  .print-message__body h3,
  .print-message__body hr,
  .print-message__body pre,
  .print-message__body table {
    margin: 0 0 1em;
  }

  .print-message__body h1 {
    font-size: 1.8em;
  }

  .print-message__body h2 {
    font-size: 1.5em;
  }

  .print-message__body h3 {
    font-size: 1.25em;
  }

  .print-message__body blockquote {
    margin-left: 0;
    padding-left: 16px;
    border-left: 4px solid rgba(0, 0, 0, 0.12);
  }

  .print-message__body hr {
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.18);
  }

  .print-message__body img {
    max-width: 100%;
    height: auto;
  }
`;

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export async function printMessageDocument({
  title,
  bodyHtml,
}: PrintMessageDocumentArgs) {
  if (typeof document === "undefined") {
    throw new Error("Printing is not available in this environment.");
  }

  const normalizedBodyHtml = normalizeMessageBodyForRender(bodyHtml);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";

  document.body.appendChild(iframe);

  try {
    const printWindow = iframe.contentWindow;
    const printDocument = printWindow?.document;

    if (!printWindow || !printDocument) {
      throw new Error("Unable to initialize the print window.");
    }

    printDocument.open();
    printDocument.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>${PRINT_STYLES}</style>
        </head>
        <body>
          <article class="print-message">
            <h1 class="print-message__title">${escapeHtml(title)}</h1>
            <div class="print-message__body">${normalizedBodyHtml}</div>
          </article>
        </body>
      </html>
    `);
    printDocument.close();

    await delay(150);
    printWindow.focus();
    printWindow.print();
    await delay(300);
  } finally {
    iframe.remove();
  }
}

export async function triggerSequentialDownloads(
  items: BrowserDownloadItem[],
  delayMs = 250,
) {
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const link = document.createElement("a");

    link.href = item.downloadUrl;
    link.rel = "noopener noreferrer";
    if (item.fileName) {
      link.download = item.fileName;
    }
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    link.remove();

    if (index < items.length - 1 && delayMs > 0) {
      await delay(delayMs);
    }
  }
}
